import { z } from "zod";
import { Locale, PASSWORD_MIN_LENGTH, PasskeySignInRequest, Role } from "./auth.js";
import { ProjectAccess } from "./projects.js";

/*
 * Governance (docs/users.md): who sees and changes what. An organisation
 * member gets rights per project: `owner` (full rights inside it) or `guest`
 * (view only); administrators see and do everything.
 */

export const MemberAccess = z.enum(["owner", "guest"]);
export type MemberAccess = z.infer<typeof MemberAccess>;

/** One project a member is given, and how. */
export const ProjectGrant = z.object({ projectId: z.uuid(), access: MemberAccess });
export type ProjectGrant = z.infer<typeof ProjectGrant>;

/**
 * What the panel calls a person: Administrator, Project owner (owns at least
 * one project, guest nowhere), Guest (guest everywhere), mixed (owner of some,
 * guest of others) or none (a member without any project yet).
 */
export const UserKind = z.enum(["admin", "project_owner", "guest", "mixed", "none"]);
export type UserKind = z.infer<typeof UserKind>;

export const UserStatus = z.enum(["active", "suspended"]);
export type UserStatus = z.infer<typeof UserStatus>;

export const UserProject = z.object({
  projectId: z.uuid(),
  projectName: z.string(),
  access: MemberAccess,
});
export type UserProject = z.infer<typeof UserProject>;

/** A person of the organisation as administrators see them in "Users and access". */
export const OrgUser = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  role: Role,
  isOrganizationOwner: z.boolean(),
  kind: UserKind,
  status: UserStatus,
  /** A passkey or an authenticator app is set up. */
  secondFactor: z.boolean(),
  lastSignInAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  /** Empty for administrators: they see every project anyway. */
  projects: z.array(UserProject),
});
export type OrgUser = z.infer<typeof OrgUser>;

export const UserSession = z.object({
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type UserSession = z.infer<typeof UserSession>;

/** One audit entry: what a person did (or what was done to them). */
export const AuditEntry = z.object({
  at: z.iso.datetime(),
  action: z.string(),
  target: z.string().nullable(),
  outcome: z.enum(["success", "denied", "failed"]),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

export const UserDetail = OrgUser.extend({
  passkeyCount: z.number().int().nonnegative(),
  totpEnabled: z.boolean(),
  sessions: z.array(UserSession),
  /** The person's own recent actions, newest first. */
  activity: z.array(AuditEntry),
});
export type UserDetail = z.infer<typeof UserDetail>;

export const Invitation = z.object({
  id: z.uuid(),
  email: z.string(),
  role: Role,
  projects: z.array(UserProject),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  createdBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  expired: z.boolean(),
});
export type Invitation = z.infer<typeof Invitation>;

export const CreateInvitationRequest = z.object({
  email: z.email().max(254),
  role: Role,
  /** Ignored for administrators, who see every project. */
  projects: z.array(ProjectGrant).max(500).default([]),
});
export type CreateInvitationRequest = z.infer<typeof CreateInvitationRequest>;

/** The invitation and its one-time link; the link is shown once and never stored in clear. */
export const CreatedInvitation = z.object({ invitation: Invitation, url: z.string() });
export type CreatedInvitation = z.infer<typeof CreatedInvitation>;

export const InvitationTokenRequest = z.object({ token: z.string().trim().min(20).max(200) });
export type InvitationTokenRequest = z.infer<typeof InvitationTokenRequest>;

/** What the invited person sees before accepting. */
export const InvitationPreview = z.object({
  email: z.string(),
  organizationName: z.string(),
  role: Role,
  expiresAt: z.iso.datetime(),
});
export type InvitationPreview = z.infer<typeof InvitationPreview>;

export const AcceptInvitationRequest = InvitationTokenRequest.extend({
  name: z.string().trim().min(1).max(100),
  password: z.string().min(PASSWORD_MIN_LENGTH).max(256),
  locale: Locale.default("en"),
});
export type AcceptInvitationRequest = z.infer<typeof AcceptInvitationRequest>;

export const UpdateUserRequest = z.object({ role: Role });
export type UpdateUserRequest = z.infer<typeof UpdateUserRequest>;

export const SetUserProjectsRequest = z.object({ projects: z.array(ProjectGrant).max(500) });
export type SetUserProjectsRequest = z.infer<typeof SetUserProjectsRequest>;

/** Who has access to a project and how ("Access" tab); administrators are listed too. */
export const ProjectMember = z.object({
  userId: z.uuid(),
  name: z.string(),
  email: z.string(),
  access: ProjectAccess,
  status: UserStatus,
});
export type ProjectMember = z.infer<typeof ProjectMember>;

export const SetProjectMemberRequest = z.object({ access: MemberAccess });
export type SetProjectMemberRequest = z.infer<typeof SetProjectMemberRequest>;

/**
 * Step-up: a fresh second-factor check before a sensitive change (roles,
 * access, second-factor reset). Either a code of the authenticator app or a
 * passkey answer to `POST /me/step-up/passkey/options`.
 */
export const StepUpRequest = z.union([
  z.object({ code: z.string().trim().min(6).max(32) }),
  PasskeySignInRequest,
]);
export type StepUpRequest = z.infer<typeof StepUpRequest>;

export const StepUpState = z.object({ until: z.iso.datetime() });
export type StepUpState = z.infer<typeof StepUpState>;
